import { SoundButton } from "../../../../common/SoundButton/SoundButton";
import { faXmark } from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import type {
    ApiDailyRewardGrant,
    ApiDailyRewardScheduleReward,
    ApiDailyRewardSnapshot,
} from "@bridge-applications/pb-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import DailyRewardCheckmarkIcon from "../../../../../assets/static/icons/daily-reward/daily-reward-checkmark-icon.svg?react";
import DailyRewardFiligreeDecorationIcon from "../../../../../assets/static/icons/daily-reward/daily-reward-filigree-decoration.svg?react";
import DailyRewardLockIcon from "../../../../../assets/static/icons/daily-reward/daily-reward-lock-icon.svg?react";
import DailyRewardShineRayIcon from "../../../../../assets/static/icons/daily-reward/daily-reward-shine-ray.svg?react";
import DailyRewardVdiv from "../../../../../assets/static/icons/daily-reward/daily-reward-vdiv.svg?react";
import gramIcon from "../../../../../assets/static/images/coins/style1/coin-style1__gram.webp";
import notIcon from "../../../../../assets/static/images/coins/style1/coin-style1__not.webp";
import dailyRewardTitleAr from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_ar.webp";
import dailyRewardTitleDe from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_de.webp";
import dailyRewardTitleEn from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_en.webp";
import dailyRewardTitleEs from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_es.webp";
import dailyRewardTitleFa from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_fa.webp";
import dailyRewardTitleFr from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_fr.webp";
import dailyRewardTitleHi from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_hi.webp";
import dailyRewardTitleId from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_id.webp";
import dailyRewardTitleIt from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_it.webp";
import dailyRewardTitleJa from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_ja.webp";
import dailyRewardTitleKo from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_ko.webp";
import dailyRewardTitlePl from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_pl.webp";
import dailyRewardTitlePtBr from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_pt-br.webp";
import dailyRewardTitleRu from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_ru.webp";
import dailyRewardTitleTr from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_tr.webp";
import dailyRewardTitleUk from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_uk.webp";
import dailyRewardTitleUz from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_uz.webp";
import dailyRewardTitleVi from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_vi.webp";
import dailyRewardTitleZhHans from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_zh-hans.webp";
import dailyRewardTitleZhHant from "../../../../../assets/static/images/daily-reward/title/daily-reward-title_zh-hant.webp";
import energyCanIcon from "../../../../../assets/static/images/symbols/v1/symbol-energy-drink.webp";
import { formatMinorUnits } from "../../../../../logic/helpers/currencyAmount";
import { haptics } from "../../../../../logic/helpers/haptics";
import { SimpleDialog } from "../../../../common/SimpleDialog/SimpleDialog";
import { SimpleDialogHeader } from "../../../../common/SimpleDialog/comps/SimpleDialogHeader";
import "./DailyRewardDialog.scss";

const DAILY_REWARD_TITLES = {
    en: dailyRewardTitleEn,
    ru: dailyRewardTitleRu,
    uk: dailyRewardTitleUk,
    es: dailyRewardTitleEs,
    "pt-BR": dailyRewardTitlePtBr,
    tr: dailyRewardTitleTr,
    ar: dailyRewardTitleAr,
    id: dailyRewardTitleId,
    de: dailyRewardTitleDe,
    fr: dailyRewardTitleFr,
    pl: dailyRewardTitlePl,
    fa: dailyRewardTitleFa,
    uz: dailyRewardTitleUz,
    vi: dailyRewardTitleVi,
    hi: dailyRewardTitleHi,
    it: dailyRewardTitleIt,
    ja: dailyRewardTitleJa,
    ko: dailyRewardTitleKo,
    "zh-Hans": dailyRewardTitleZhHans,
    "zh-Hant": dailyRewardTitleZhHant,
} as const;

type DayStatus = "past" | "today" | "future";

interface DailyRewardDialogProps {
    isOpen: boolean;
    snapshot: ApiDailyRewardSnapshot | null;
    grants: ApiDailyRewardGrant[];
    loadStatus: "idle" | "loading" | "ready" | "error";
    loadError: string | null;
    claimState: "idle" | "claimable" | "claimed";
    onClose: () => void;
    onClaim: () => void;
    onRetry: () => void;
}

function rewardIcon(reward: ApiDailyRewardScheduleReward): string {
    if (reward.kind === "SM_ENERGY") return energyCanIcon;
    return reward.currencyCode === "GRAM" ? gramIcon : notIcon;
}

function rewardAmount(
    reward: ApiDailyRewardScheduleReward,
    locale: string,
): string {
    if (reward.kind === "SM_ENERGY") return reward.amount;
    return formatMinorUnits(reward.amount, reward.decimalScale, locale);
}

export function DailyRewardDialog({
    isOpen,
    snapshot,
    grants,
    loadStatus,
    loadError,
    claimState,
    onClose,
    onClaim,
    onRetry,
}: DailyRewardDialogProps) {
    const { t, i18n } = useTranslation();
    const titleRef = useRef<HTMLSpanElement>(null);
    const locale = i18n.resolvedLanguage ?? i18n.language;
    const titleImage =
        DAILY_REWARD_TITLES[locale as keyof typeof DAILY_REWARD_TITLES] ??
        DAILY_REWARD_TITLES.en;
    const grantedKeys = new Set(grants.map((grant) => grant.key));

    const claimReward = () => {
        if (claimState !== "claimable") return;
        haptics.impact("light");
        onClaim();
    };

    const handleDialogClick = (event: MouseEvent<HTMLDialogElement>) => {
        if (claimState !== "claimable") return;

        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest(".simple-dialog__content")) return;
        if (
            target.closest(
                "button, a, input, select, textarea, summary, [role='button']",
            )
        ) {
            return;
        }

        claimReward();
    };

    return (
        <SimpleDialog
            className="daily-reward-dialog"
            isOpen={isOpen}
            initialFocusRef={titleRef}
            onClose={onClose}
            onClick={handleDialogClick}
            closeOnBackdropClick={false}
            data-claim-status={claimState}
            aria-labelledby="daily-reward-dialog-title"
            header={
                <SimpleDialogHeader
                    className="daily-reward-dialog__header"
                    title={
                        <>
                            <img
                                src={titleImage}
                                alt=""
                                aria-hidden="true"
                                draggable={false}
                            />
                            <span
                                ref={titleRef}
                                className="daily-reward-dialog__accessible-title"
                                tabIndex={-1}
                            >
                                {t("dailyReward.title")}
                            </span>
                        </>
                    }
                    titleId="daily-reward-dialog-title"
                    closeLabel={t("dailyReward.closeLabel")}
                    closeIcon={<FontAwesomeIcon icon={faXmark} />}
                    onClose={onClose}
                />
            }
        >
            <svg
                className="daily-reward-dialog__filter-definitions"
                aria-hidden="true"
                focusable="false"
            >
                <defs>
                    <filter id="daily-reward-dialog-shine-ray-blur">
                        <feGaussianBlur stdDeviation="0.4" />
                    </filter>
                </defs>
            </svg>

            <DailyRewardFiligreeDecorationIcon
                className="daily-reward-dialog__filigree daily-reward-dialog__filigree--top-left"
                aria-hidden="true"
            />
            <DailyRewardFiligreeDecorationIcon
                className="daily-reward-dialog__filigree daily-reward-dialog__filigree--top-right"
                aria-hidden="true"
            />
            {/* <DailyRewardFiligreeDecorationIcon
                className="daily-reward-dialog__filigree daily-reward-dialog__filigree--bottom-left"
                aria-hidden="true"
            />
            <DailyRewardFiligreeDecorationIcon
                className="daily-reward-dialog__filigree daily-reward-dialog__filigree--bottom-right"
                aria-hidden="true"
            /> */}

            <div className="daily-reward-dialog__body">
                <div
                    className="daily-reward-dialog__divider"
                    aria-hidden="true"
                >
                    <DailyRewardVdiv />
                </div>

                {snapshot ? (
                    <>
                        <ol
                            className="daily-reward-dialog__schedule"
                            aria-label={t("dailyReward.scheduleLabel")}
                        >
                            {snapshot.schedule.map((scheduleStep) => {
                                const isCurrent =
                                    scheduleStep.step === snapshot.currentStep;
                                const currentRewardsGranted =
                                    scheduleStep.rewards.every((reward) =>
                                        grantedKeys.has(reward.key),
                                    );
                                const isCurrentClaimed =
                                    isCurrent &&
                                    claimState === "claimed" &&
                                    currentRewardsGranted;
                                const dayStatus: DayStatus =
                                    scheduleStep.step < snapshot.currentStep ||
                                    isCurrentClaimed
                                        ? "past"
                                        : isCurrent
                                          ? "today"
                                          : "future";
                                const span = Math.min(
                                    Math.max(scheduleStep.rewards.length, 1),
                                    3,
                                );

                                return (
                                    <li
                                        key={scheduleStep.step}
                                        className="daily-reward-dialog__day-cell"
                                        data-span={span}
                                        data-status={dayStatus}
                                        aria-current={
                                            dayStatus === "today"
                                                ? "step"
                                                : undefined
                                        }
                                    >
                                        <div
                                            className="daily-reward-dialog__day"
                                            data-status={dayStatus}
                                        >
                                            <div className="daily-reward-dialog__day-inner">
                                                <div className="daily-reward-dialog__day-title">
                                                    <p>
                                                        {dayStatus === "today"
                                                            ? t(
                                                                  "dailyReward.today",
                                                              )
                                                            : t(
                                                                  "dailyReward.day",
                                                                  {
                                                                      day: scheduleStep.step,
                                                                  },
                                                              )}
                                                    </p>
                                                </div>

                                                <div className="daily-reward-dialog__rewards">
                                                    {scheduleStep.rewards.map(
                                                        (reward) => {
                                                            const amount =
                                                                rewardAmount(
                                                                    reward,
                                                                    locale,
                                                                );
                                                            const leadingZero =
                                                                /^0([.,])(.*)$/.exec(
                                                                    amount,
                                                                );

                                                            return (
                                                                <div
                                                                    key={
                                                                        reward.key
                                                                    }
                                                                    className="daily-reward-dialog__reward"
                                                                >
                                                                    <div
                                                                        className={`daily-reward-dialog__coin-wrap ${reward.kind.toLowerCase()}`}
                                                                    >
                                                                        {dayStatus ===
                                                                            "today" && (
                                                                            <div
                                                                                className="daily-reward-dialog__shine"
                                                                                aria-hidden="true"
                                                                            >
                                                                                {Array.from(
                                                                                    {
                                                                                        length: 10,
                                                                                    },
                                                                                ).map(
                                                                                    (
                                                                                        _,
                                                                                        index,
                                                                                    ) => (
                                                                                        <DailyRewardShineRayIcon
                                                                                            key={
                                                                                                index
                                                                                            }
                                                                                            className="daily-reward-dialog__shine-ray"
                                                                                            style={{
                                                                                                transform: `translate(-50%, 0) rotate(${index * 36}deg)`,
                                                                                            }}
                                                                                        />
                                                                                    ),
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        <img
                                                                            className="daily-reward-dialog__coin"
                                                                            src={rewardIcon(
                                                                                reward,
                                                                            )}
                                                                            alt=""
                                                                            aria-hidden="true"
                                                                            draggable={
                                                                                false
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <span
                                                                        className="daily-reward-dialog__amount"
                                                                        dir="ltr"
                                                                    >
                                                                        {leadingZero ? (
                                                                            <>
                                                                                <span className="daily-reward-dialog__amount-leading-zero">
                                                                                    {`0${leadingZero[1]}`}
                                                                                </span>
                                                                                {
                                                                                    leadingZero[2]
                                                                                }
                                                                            </>
                                                                        ) : (
                                                                            amount
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            );
                                                        },
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {dayStatus === "future" && (
                                            <DailyRewardLockIcon
                                                className="daily-reward-dialog__lock-icon"
                                                aria-hidden="true"
                                            />
                                        )}
                                        {dayStatus === "past" && (
                                            <DailyRewardCheckmarkIcon
                                                className="daily-reward-dialog__check-icon"
                                                aria-hidden="true"
                                            />
                                        )}
                                    </li>
                                );
                            })}
                        </ol>

                        <SoundButton
                            type="button"
                            className="daily-reward-dialog__claim"
                            data-claim-status={claimState}
                            disabled={claimState !== "claimable"}
                            onClick={claimReward}
                        >
                            {claimState === "claimed"
                                ? t("dailyReward.claimed")
                                : t("dailyReward.tapToClaim")}
                        </SoundButton>
                    </>
                ) : loadStatus === "error" ? (
                    <div className="daily-reward-dialog__status" role="alert">
                        <p>{loadError ?? t("dailyReward.error")}</p>
                        <SoundButton type="button" onClick={onRetry}>
                            {t("dailyReward.retry")}
                        </SoundButton>
                    </div>
                ) : (
                    <div className="daily-reward-dialog__status" role="status">
                        <span className="daily-reward-dialog__spinner" />
                        <p>{t("dailyReward.loading")}</p>
                    </div>
                )}
            </div>
        </SimpleDialog>
    );
}
